import { Switch } from '../../../../components';
import { createQuestionFromConfiguration } from '../../../logbook/views/QuestionView';
import {
    BulletList,
    Card,
    Exercise,
    LabelBody,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    Textarea,
} from '../../components';
import { Module2S2Exercise1Illustration1 } from '../../icons';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import { VideoExerciseView } from '../../views';
import { ExerciseData, ExerciseViewProps } from '../exercises';

const page1HasSeenVideoKey = 'exercise221Page1HasSeenVideoKey';
const page3HasSeenVideoKey = 'exercise221Page3HasSeenVideoKey';
const page5HasSeenVideoKey = 'exercise221Page5HasSeenVideoKey';
const page2HowWouldYouHelpAFriendKey =
    'exercise221Page2HowWouldYouHelpAFriendKey';
const page4HowWouldYouHelpYourselfKey =
    'exercise221Page4HowWouldYouHelpYourselfKey';
const page6ReflectOnTheExerciseKey = 'exercise221Page6ReflectOnTheExerciseKey';

export const exerciseData221: ExerciseData = {
    ExerciseView: Module2S2Exercise1,
    dataKeys: [
        page2HowWouldYouHelpAFriendKey,
        page4HowWouldYouHelpYourselfKey,
        page6ReflectOnTheExerciseKey,
    ],
    questions: [
        createQuestionFromConfiguration({
            dataKeys: [page2HowWouldYouHelpAFriendKey],
            title: 'Hvordan ville du hjælpe en ven?',
            description:
                'Tænk over, hvordan du ville forsøge at være der for personen. Hvad ville du sige, og hvordan ville du hjælpe?',
        }),
        createQuestionFromConfiguration({
            dataKeys: [page4HowWouldYouHelpYourselfKey],
            title: 'Hvordan ville du hjælpe dig selv?',
            description:
                'Tænk nu over, hvordan du ville forsøge at være der for den udgave af dig, der kan have det svært. Hvad ville du sige, og hvordan ville du hjælpe?',
        }),
        createQuestionFromConfiguration({
            dataKeys: [page6ReflectOnTheExerciseKey],
            title: 'Reflekter over øvelsen',
            description:
                'Overvej nu, om der er forskel på den måde, du vil hjælpe en ven på sammenlignet med dig selv. Hvad kan du tage med dig fra øvelsen, så du bedre husker en større forståelse og omsorg for dig selv?',
        }),
    ],
};

export function Module2S2Exercise1({
    exercise,
    onContinue,
}: ExerciseViewProps) {
    const [
        page2HowWouldYouHelpAFriendInput,
        setPage2HowWouldYouHelpAFriendInput,
    ] = useSyncedState(exercise.Data[page2HowWouldYouHelpAFriendKey] ?? '');

    const [
        page4HowWouldYouHelpYourselfInput,
        setPage4HowWouldYouHelpYourselfInput,
    ] = useSyncedState(exercise.Data[page4HowWouldYouHelpYourselfKey] ?? '');

    const [page6ReflectOnTheExerciseInput, setPage6ReflectOnTheExerciseInput] =
        useSyncedState(exercise.Data[page6ReflectOnTheExerciseKey] ?? '');

    return (
        <Exercise title="På roadtrip med en ven">
            <ProgressBar current={exercise.CurrentPage} total={7} />
            <Switch
                value={exercise.CurrentPage}
                default={1}
                cases={{
                    1: (
                        <Page1
                            exercise={exercise}
                            page1HasSeenVideo={
                                exercise.Data[page1HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [page1HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            page2HowWouldYouHelpAFriendInput={
                                page2HowWouldYouHelpAFriendInput
                            }
                            onChangePage2HowWouldYouHelpAFriendInput={
                                setPage2HowWouldYouHelpAFriendInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 3,
                                    data: {
                                        [page2HowWouldYouHelpAFriendKey]:
                                            normalizeInput(
                                                page2HowWouldYouHelpAFriendInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    3: (
                        <Page3
                            exercise={exercise}
                            page3HasSeenVideo={
                                exercise.Data[page3HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 4,
                                    data: {
                                        [page3HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    4: (
                        <Page4
                            page4HowWouldYouHelpYourselfInput={
                                page4HowWouldYouHelpYourselfInput
                            }
                            onChangePage4HowWouldYouHelpYourselfInput={
                                setPage4HowWouldYouHelpYourselfInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 5,
                                    data: {
                                        [page4HowWouldYouHelpYourselfKey]:
                                            normalizeInput(
                                                page4HowWouldYouHelpYourselfInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    5: (
                        <Page5
                            exercise={exercise}
                            page5HasSeenVideo={
                                exercise.Data[page5HasSeenVideoKey] ?? 'false'
                            }
                            onContinue={() => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 6,
                                    data: {
                                        [page5HasSeenVideoKey]: 'true',
                                    },
                                });
                            }}
                        />
                    ),
                    6: (
                        <Page6
                            page6ReflectOnTheExerciseInput={
                                page6ReflectOnTheExerciseInput
                            }
                            onChangePage6ReflectOnTheExerciseInput={
                                setPage6ReflectOnTheExerciseInput
                            }
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 7,
                                    data: {
                                        [page6ReflectOnTheExerciseKey]:
                                            normalizeInput(
                                                page6ReflectOnTheExerciseInput,
                                            ),
                                    },
                                })
                            }
                        />
                    ),
                    7: (
                        <Page7
                            onContinue={() =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                })
                            }
                        />
                    ),
                }}
            />
        </Exercise>
    );
}

type PageProps = {
    onContinue: () => void;
};

type Page1Props = {
    exercise: ExerciseDto;
    page1HasSeenVideo: string;
    onContinue: () => void;
};

function Page1({ exercise, page1HasSeenVideo, onContinue }: Page1Props) {
    return (
        <>
            <LabelDisplay2>Hvordan viser du omsorg?</LabelDisplay2>
            <LabelBody class="my-10">
                Ofte behandler vi os selv helt anderledes, end vi ville behandle
                dem vi holder af og som er vigtige for os. At blive mere bevidst
                om, hvordan du kan vise omsorg for dig selv, er et værktøj til
                ikke at dømme eller kritisere dig selv for hårdt. Det kan være
                en vej til at mindske niveauet af bekymringer og tankemylder.
            </LabelBody>
            <LabelBody>Videoen guider dig igennem øvelsen.</LabelBody>
            <VideoExerciseView
                className="my-30"
                exercise={exercise}
                videoMetadata={{
                    PosterUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                    VideoUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+A+-+V05+-+Online+-+241220+-+JK.mp4',
                    MimeType: 'video/mp4',
                    Duration: '00:00:58',
                    Width: 1920,
                    Height: 1080,
                }}
                onVideoEnded={() => {
                    onContinue();
                }}
            />
            <Spacer />
            {page1HasSeenVideo === 'true' && (
                <SecondaryButton
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </>
    );
}

type Page2Props = {
    page2HowWouldYouHelpAFriendInput: string;
    onChangePage2HowWouldYouHelpAFriendInput: (value: string) => void;
    onContinue: () => void;
};

function Page2({
    page2HowWouldYouHelpAFriendInput,
    onChangePage2HowWouldYouHelpAFriendInput,
    onContinue,
}: Page2Props) {
    return (
        <>
            <Card class="mb-30">
                <LabelDisplay1 class="mb-10">
                    Hvordan ville du hjælpe en ven?
                </LabelDisplay1>
                <LabelBody>
                    Tænk over, hvordan du ville forsøge at være der for
                    personen.
                    <BulletList
                        className="mt-10"
                        items={[
                            'Hvad ville du sige, og hvordan ville du hjælpe?',
                        ]}
                    />
                </LabelBody>
                <Textarea
                    className="mt-20"
                    text={page2HowWouldYouHelpAFriendInput}
                    onChangeText={onChangePage2HowWouldYouHelpAFriendInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page3Props = {
    exercise: ExerciseDto;
    page3HasSeenVideo: string;
    onContinue: () => void;
};

function Page3({ exercise, page3HasSeenVideo, onContinue }: Page3Props) {
    return (
        <div className="three-blobs-img-2 flex flex-1 flex-col">
            <Spacer />
            <LabelDisplay2 class="mx-auto">
                Fortsæt videoklippet...
            </LabelDisplay2>
            <VideoExerciseView
                className="mb-30 mt-20"
                exercise={exercise}
                videoMetadata={{
                    PosterUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                    VideoUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+B+-+V05+-+Online+-+241220+-+JK.mp4',
                    MimeType: 'video/mp4',
                    Duration: '00:00:38',
                    Width: 1920,
                    Height: 1080,
                }}
                onVideoEnded={() => {
                    onContinue();
                }}
            />
            <Spacer />
            {page3HasSeenVideo === 'true' && (
                <SecondaryButton
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </div>
    );
}

type Page4Props = {
    page4HowWouldYouHelpYourselfInput: string;
    onChangePage4HowWouldYouHelpYourselfInput: (value: string) => void;
    onContinue: () => void;
};

function Page4({
    page4HowWouldYouHelpYourselfInput,
    onChangePage4HowWouldYouHelpYourselfInput,
    onContinue,
}: Page4Props) {
    return (
        <>
            <Card class="mb-30">
                <LabelDisplay1 class="mb-10">
                    Hvordan ville du hjælpe dig selv?
                </LabelDisplay1>
                <LabelBody>
                    Tænk nu over, hvordan du ville forsøge at være der for den
                    udgave af dig, der kan have det svært.
                    <BulletList
                        className="mt-10"
                        items={[
                            'Hvad ville du sige, og hvordan ville du hjælpe?',
                        ]}
                    />
                </LabelBody>
                <Textarea
                    className="mt-20"
                    text={page4HowWouldYouHelpYourselfInput}
                    onChangeText={onChangePage4HowWouldYouHelpYourselfInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

type Page5Props = {
    exercise: ExerciseDto;
    page5HasSeenVideo: string;
    onContinue: () => void;
};

function Page5({ exercise, page5HasSeenVideo, onContinue }: Page5Props) {
    return (
        <div className="three-blobs-img-3 flex flex-1 flex-col">
            <Spacer />
            <LabelDisplay2 class="mx-auto">
                Fortsæt videoklippet...
            </LabelDisplay2>
            <VideoExerciseView
                className="mb-30 mt-20"
                exercise={exercise}
                videoMetadata={{
                    PosterUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+-+V05+-+Online+-+241220+-+JK.00_00_43_03.Still007.jpg',
                    VideoUrl:
                        'https://static.hejdoktor.dk/falcksundtsind/Falck+-+Sundt+Sind+-+Film+07+C+-+V05+-+Online+-+241220+-+JK.mp4',
                    MimeType: 'video/mp4',
                    Duration: '00:00:36',
                    Width: 1920,
                    Height: 1080,
                }}
                onVideoEnded={() => {
                    onContinue();
                }}
            />
            <Spacer />
            {page5HasSeenVideo === 'true' && (
                <SecondaryButton
                    onClick={() => {
                        onContinue();
                    }}
                >
                    Fortsæt øvelsen
                </SecondaryButton>
            )}
        </div>
    );
}

type Page6Props = {
    page6ReflectOnTheExerciseInput: string;
    onChangePage6ReflectOnTheExerciseInput: (value: string) => void;
    onContinue: () => void;
};

function Page6({
    page6ReflectOnTheExerciseInput,
    onChangePage6ReflectOnTheExerciseInput,
    onContinue,
}: Page6Props) {
    return (
        <>
            <Card class="mb-30">
                <LabelDisplay1 class="mb-10">
                    Reflekter over øvelsen
                </LabelDisplay1>
                <LabelBody>
                    Overvej nu, om der er forskel på den måde, du vil hjælpe en
                    ven på sammenlignet med dig selv.
                    <BulletList
                        className="mt-10"
                        items={[
                            'Hvad kan du tage med dig fra øvelsen, så du bedre husker en større forståelse og omsorg for dig selv?',
                        ]}
                    />
                </LabelBody>
                <Textarea
                    className="mt-20"
                    text={page6ReflectOnTheExerciseInput}
                    onChangeText={onChangePage6ReflectOnTheExerciseInput}
                />
                <LabelBody>
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Fortsæt øvelsen
            </SecondaryButton>
        </>
    );
}

function Page7({ onContinue }: PageProps) {
    return (
        <>
            <LabelDisplay2 class="mb-10">Afrunding</LabelDisplay2>
            <LabelBody>
                Husk at du er ikke alene, alle kan have det svært. Du er bare et
                menneske – hverken mindre eller mere. Næste gang du f.eks.
                oplever at være opslugt af tanker, selvkritik eller det, der kan
                være svært, så prøv de ting af, du har skrevet ned. Det kan have
                en beroligende effekt.
            </LabelBody>
            <Module2S2Exercise1Illustration1 className="mx-auto my-20" />
            <Spacer />
            <SecondaryButton onClick={onContinue}>
                Afslut øvelsen
            </SecondaryButton>
        </>
    );
}
